/**
 * "Your Donation Summary" — the recurring card on the checkout screens.
 *
 * Written once and reused on Donate a Meal, Donor Information, Payment,
 * Review and Thank You, so the five renderings cannot diverge.
 *
 * The total it shows is whatever it is handed. Callers pass the server quote,
 * never a locally multiplied figure.
 */

import { memo } from 'react';
import { Image, StyleSheet, View } from 'react-native';

import { formatDate, formatMoney, formatPeople, type Money } from '@mfa/domain';
import { mealImageSource } from '../../assets';
import { colors, radius, space } from '../../theme';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { Glyph } from '../common/Icon';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

export interface DonationSummaryCardProps {
  title?: string;
  mealName: string;
  imageUrl?: string | null;
  peopleCount: number;
  unitPrice?: Money | null;
  totalAmount: Money;
  distributionDate?: string | null;
  /** Renders the "Edit" affordance seen on every summary in the designs. */
  onEdit?: () => void;
  editLabel?: string;
  testID?: string;
}

export const DonationSummaryCard = memo(function DonationSummaryCard({
  title = 'Donation Summary',
  mealName,
  imageUrl,
  peopleCount,
  unitPrice,
  totalAmount,
  distributionDate,
  onEdit,
  editLabel,
  testID = 'donation-summary',
}: DonationSummaryCardProps) {
  const t = useT();
  const edit = editLabel ?? t('Edit');
  return (
    <Card
      title={title}
      testID={testID}
      action={
        onEdit ? (
          <Button
            label={edit}
            onPress={onEdit}
            variant="tertiary"
            size="md"
            fullWidth={false}
            testID={`${testID}-edit`}
          />
        ) : undefined
      }
    >
      <View style={styles.row}>
        {(() => {
          const source = mealImageSource(imageUrl, mealName);
          return source ? (
            <Image source={source} style={styles.image} accessibilityIgnoresInvertColors />
          ) : (
            <View style={[styles.image, styles.imageFallback]} />
          );
        })()}

        <View style={styles.details}>
          <Text variant="cardTitle" numberOfLines={2}>
            {mealName}
          </Text>

          <View style={styles.metaRow}>
            <Glyph name="people" size={14} />
            <Text variant="caption" tone="secondary">
              {formatPeople(peopleCount)}
            </Text>
          </View>

          {unitPrice ? (
            <View style={styles.metaRow}>
              <Glyph name="price" size={14} />
              <Text variant="caption" tone="secondary">
                {formatMoney(unitPrice)} {t('per person')}
              </Text>
            </View>
          ) : null}

          {distributionDate ? (
            <View style={styles.metaRow}>
              <Glyph name="calendar" size={14} />
              <Text variant="caption" tone="secondary">
                {formatDate(distributionDate)}
              </Text>
            </View>
          ) : null}
        </View>

        <View style={styles.totalBlock}>
          <Text variant="caption" tone="tertiary" align="right">{t('Total Amount')}</Text>
          <Text variant="amount" tone="brand" align="right" testID={`${testID}-total`}>
            {formatMoney(totalAmount)}
          </Text>
        </View>
      </View>
    </Card>
  );
});

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: space.md,
    alignItems: 'flex-start',
  },
  image: {
    width: 76,
    height: 76,
    borderRadius: radius.md,
    resizeMode: 'cover',
  },
  imageFallback: {
    backgroundColor: colors.bg.surfaceSunken,
  },
  details: {
    flex: 1,
    gap: space.xxs,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
  },
  totalBlock: {
    alignItems: 'flex-end',
    gap: space.xxs,
  },
});
